import React, { useState, useEffect, useCallback } from "react";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  PaginationComponent,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  DataTableItem,
  Button,
  RSelect,
} from "components/Component";
import Content from "layout/content/Content";
import Head from "layout/head/Head";
import { Card, CardBody, Badge, Modal, ModalBody, FormGroup, Label, Input, Spinner } from "reactstrap";
import { useCookies } from "react-cookie";
import Swal from "sweetalert2";
import moment from "moment";
import {
  serviceGetBlockRequests,
  serviceApproveBlockRequest,
  serviceRejectBlockRequest,
  serviceUnblockRequest,
} from "services/contactBlock";

const STATUS_OPTIONS = [
  { value: "PENDING_APPROVAL", label: "Menunggu Persetujuan" },
  { value: "APPROVED", label: "Disetujui" },
  { value: "REJECTED", label: "Ditolak" },
];

const STATUS_BADGE_COLOR = {
  PENDING_APPROVAL: "warning",
  APPROVED: "success",
  REJECTED: "danger",
};

const ContactBlockApproval = ({ embedded = false }) => {
  const [cookies] = useCookies(["user"]);
  const canReview = cookies.role.name && cookies.role.name !== "SALES";

  const [data, setData] = useState([]);
  const [metaData, setMetaData] = useState({});
  const [status, setStatus] = useState("PENDING_APPROVAL");
  const [isLoading, setIsLoading] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");

  const refreshData = useCallback(
    async (page = 1) => {
      if (!canReview) return;
      setIsLoading(true);
      const resp = await serviceGetBlockRequests(cookies.SIDikey, { status, page });
      setIsLoading(false);
      if (resp.status === 200) {
        setData(resp.data.data);
        setMetaData(resp.data.meta);
      }
    },
    [canReview, cookies.SIDikey, status],
  );

  useEffect(() => {
    refreshData(1);
  }, [refreshData]);

  const handleApprove = async (item) => {
    const confirm = await Swal.fire({
      title: "Setujui blokir nomor ini?",
      text: `${item.phoneNumber}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Setujui",
      cancelButtonText: "Batal",
    });
    if (!confirm.isConfirmed) return;

    setProcessingId(item.id);
    const resp = await serviceApproveBlockRequest(cookies.SIDikey, item.id);
    setProcessingId(null);

    if (resp.status === 202) {
      Swal.fire("Berhasil", "Nomor telah diblokir", "success");
      refreshData(metaData?.page || 1);
    } else {
      Swal.fire("Gagal", resp?.data?.message || "Mohon coba lagi", "error");
    }
  };

  const handleReject = async () => {
    if (!rejectionReason) {
      Swal.fire("Lengkapi form", "Alasan penolakan wajib diisi", "warning");
      return;
    }

    setProcessingId(rejectTarget.id);
    const resp = await serviceRejectBlockRequest(cookies.SIDikey, rejectTarget.id, rejectionReason);
    setProcessingId(null);

    if (resp.status === 202) {
      Swal.fire("Berhasil", "Pengajuan telah ditolak", "success");
      setRejectTarget(null);
      setRejectionReason("");
      refreshData(metaData?.page || 1);
    } else {
      Swal.fire("Gagal", resp?.data?.message || "Mohon coba lagi", "error");
    }
  };

  const handleUnblock = async (item) => {
    const confirm = await Swal.fire({
      title: "Unblock nomor ini?",
      text: `${item.phoneNumber}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Unblock",
      cancelButtonText: "Batal",
    });
    if (!confirm.isConfirmed) return;

    setProcessingId(item.id);
    const resp = await serviceUnblockRequest(cookies.SIDikey, item.id);
    setProcessingId(null);

    if (resp.status === 202) {
      Swal.fire("Berhasil", "Nomor telah di-unblock", "success");
      refreshData(metaData?.page || 1);
    } else {
      Swal.fire("Gagal", resp?.data?.message || "Mohon coba lagi", "error");
    }
  };

  const noAccess = (
    <Card className="card-bordered">
      <CardBody>Anda tidak memiliki akses ke halaman ini.</CardBody>
    </Card>
  );

  if (!canReview) {
    return embedded ? (
      noAccess
    ) : (
      <React.Fragment>
        <Head title="Approval Blokir Nomor"></Head>
        <Content>{noAccess}</Content>
      </React.Fragment>
    );
  }

  const body = (
    <React.Fragment>
      {!embedded && (
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Approval Blokir Nomor
              </BlockTitle>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
      )}
      <Block>
        <DataTable className="card-stretch">
          <div className="card-inner position-relative card-tools-toggle">
            <div className="card-title-group">
              <div className="role_search_input_container">
                <div className="role_search_input" style={{ minWidth: "220px" }}>
                  <RSelect
                    placeholder="Filter Status"
                    value={STATUS_OPTIONS.find((opt) => opt.value === status)}
                    options={STATUS_OPTIONS}
                    onChange={(e) => setStatus(e.value)}
                  />
                </div>
              </div>
            </div>
          </div>
          <DataTableBody>
            <DataTableHead>
              <DataTableRow className="nk-tb-col-check">
                <span className="sub-text">No</span>
              </DataTableRow>
              <DataTableRow size="md">
                <span className="sub-text">Nomor</span>
              </DataTableRow>
              <DataTableRow size="md">
                <span className="sub-text">Diajukan Oleh</span>
              </DataTableRow>
              <DataTableRow size="md">
                <span className="sub-text">Alasan</span>
              </DataTableRow>
              <DataTableRow size="md">
                <span className="sub-text">Bukti</span>
              </DataTableRow>
              <DataTableRow size="md">
                <span className="sub-text">Status</span>
              </DataTableRow>
              <DataTableRow size="md">
                <span className="sub-text">Diajukan Pada</span>
              </DataTableRow>
              {(status === "PENDING_APPROVAL" || status === "APPROVED") && (
                <DataTableRow size="md">
                  <span className="sub-text">Aksi</span>
                </DataTableRow>
              )}
            </DataTableHead>
            {!isLoading && data.length > 0
              ? data.map((item, idx) => (
                  <DataTableItem key={item.id}>
                    <DataTableRow size="mb">
                      <span className="tb-amount">{(metaData.page - 1) * metaData.take + (idx + 1)}</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span className="tb-amount">{`${item.phoneNumber}`}</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span className="tb-amount">
                        {item.createdBy ? item.createdBy.firstName + " " + item.createdBy.lastName : "-"}
                      </span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span className="tb-amount">{item.reasonDescription}</span>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <a href={item.evidenceFileUrl} target="_blank" rel="noopener noreferrer">
                        Lihat
                      </a>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <Badge color={STATUS_BADGE_COLOR[item.status]} pill>
                        {item.status}
                      </Badge>
                    </DataTableRow>
                    <DataTableRow size="md">
                      <span className="tb-amount">{moment(item.createdAt).format("DD MMM YYYY, HH:mm")}</span>
                    </DataTableRow>
                    {status === "PENDING_APPROVAL" && (
                      <DataTableRow size="md">
                        <Button
                          color="success"
                          size="sm"
                          className="mr-1"
                          disabled={processingId === item.id}
                          onClick={() => handleApprove(item)}
                        >
                          {processingId === item.id ? <Spinner size="sm" /> : "Setujui"}
                        </Button>
                        <Button
                          color="danger"
                          size="sm"
                          outline
                          disabled={processingId === item.id}
                          onClick={() => setRejectTarget(item)}
                        >
                          Tolak
                        </Button>
                      </DataTableRow>
                    )}
                    {status === "APPROVED" && (
                      <DataTableRow size="md">
                        {item.unblockedAt ? (
                          <span className="text-silent">
                            Sudah di-unblock ({moment(item.unblockedAt).format("DD MMM YYYY, HH:mm")})
                          </span>
                        ) : (
                          <Button
                            color="secondary"
                            size="sm"
                            outline
                            disabled={processingId === item.id}
                            onClick={() => handleUnblock(item)}
                          >
                            {processingId === item.id ? <Spinner size="sm" /> : "Unblock"}
                          </Button>
                        )}
                      </DataTableRow>
                    )}
                  </DataTableItem>
                ))
              : null}
          </DataTableBody>
          <div className="card-inner">
            {!isLoading && data.length > 0 ? (
              <PaginationComponent
                itemPerPage={metaData?.take}
                totalItems={metaData?.itemCount}
                paginate={(page) => refreshData(page)}
                currentPage={metaData?.page}
              />
            ) : (
              <div className="text-center">
                <span className="text-silent">{isLoading ? "Memuat..." : "Tidak ada data"}</span>
              </div>
            )}
          </div>
        </DataTable>
      </Block>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      {embedded ? (
        body
      ) : (
        <React.Fragment>
          <Head title="Approval Blokir Nomor"></Head>
          <Content>{body}</Content>
        </React.Fragment>
      )}

      <Modal isOpen={!!rejectTarget} toggle={() => setRejectTarget(null)} className="modal-dialog-centered">
        <ModalBody>
          <a
            href="#cancel"
            onClick={(ev) => {
              ev.preventDefault();
              setRejectTarget(null);
            }}
            className="close"
          >
            <em className="icon ni ni-cross-sm"></em>
          </a>
          <h5 className="mb-3">Tolak Pengajuan Blokir</h5>
          <p className="sub-text mb-3">{rejectTarget && rejectTarget.phoneNumber}</p>
          <FormGroup>
            <Label>Alasan Penolakan</Label>
            <Input
              type="textarea"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Jelaskan alasan penolakan"
            />
          </FormGroup>
          <Button color="danger" block disabled={processingId === rejectTarget?.id} onClick={handleReject}>
            {processingId === rejectTarget?.id ? <Spinner size="sm" /> : "Tolak Pengajuan"}
          </Button>
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ContactBlockApproval;
